import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  EmptyHubState,
  MiniLoader,
  PipelinePills,
  StatusPill,
  type PipelineStage,
  type StatusPillTone,
} from '../../Common'
import { useNls } from '../../../nls/nlsProvider'
import {
  listEduEnquiries,
  setEduEnquiryStage,
  addEduEnquiryNote,
  type EduEnquiry,
  type EduEnquiryStage,
} from '../../../services/eduEnquiryService'
import { AdmitEnquiryDialog } from '../../Education/AdmitEnquiryDialog'
import type { TimelineElement } from '../../../types/timelineElement'

export interface ClassAdmissionsPanelProps {
  workspaceId: string
  classId?: string
  elements?: TimelineElement[]
}

const STAGES: EduEnquiryStage[] = [
  'NEW',
  'CONTACTED',
  'TRIAL_BOOKED',
  'TRIAL_DONE',
  'ADMITTED',
]

const getStageTone = (stage: EduEnquiryStage): StatusPillTone => {
  switch (stage) {
    case 'NEW':
      return 'info'
    case 'CONTACTED':
      return 'warning'
    case 'TRIAL_BOOKED':
    case 'TRIAL_DONE':
      return 'info'
    case 'ADMITTED':
      return 'success'
    case 'LOST':
      return 'muted'
    default:
      return 'neutral'
  }
}

export const ClassAdmissionsPanel: React.FC<ClassAdmissionsPanelProps> = ({
  workspaceId,
  classId,
  elements = [],
}) => {
  const { t } = useNls()

  const [enquiries, setEnquiries] = useState<EduEnquiry[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedStage, setSelectedStage] = useState<string>('ALL')

  // Target enquiry for opening the existing AdmitEnquiryDialog
  const [admittingEnquiry, setAdmittingEnquiry] = useState<EduEnquiry | null>(null)
  const [updatingId, setUpdatingId] = useState<string | null>(null)

  const loadEnquiries = async () => {
    if (!workspaceId) return
    setLoading(true)
    setError(null)
    try {
      const res = await listEduEnquiries({
        workspaceId,
        interestNodeId: classId || undefined,
      })
      setEnquiries(res.enquiries || [])
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Failed to load enquiries')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadEnquiries()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, classId])

  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = { ALL: enquiries.length }
    for (const s of STAGES) {
      counts[s] = 0
    }
    for (const enq of enquiries) {
      if (counts[enq.stage] !== undefined) {
        counts[enq.stage]++
      }
    }
    return counts
  }, [enquiries])

  const pipelineStages: PipelineStage[] = useMemo(() => {
    return [
      { key: 'ALL', label: t('NLSEduAdmissionsFilterSourceAll'), count: stageCounts.ALL || 0 },
      { key: 'NEW', label: t('NLSEduAdmissionsStageNew'), count: stageCounts.NEW || 0 },
      { key: 'CONTACTED', label: t('NLSEduAdmissionsStageContacted'), count: stageCounts.CONTACTED || 0 },
      { key: 'TRIAL_BOOKED', label: t('NLSEduAdmissionsStageTrialBooked'), count: stageCounts.TRIAL_BOOKED || 0 },
      { key: 'TRIAL_DONE', label: t('NLSEduAdmissionsStageTrialDone'), count: stageCounts.TRIAL_DONE || 0 },
      { key: 'ADMITTED', label: t('NLSEduAdmissionsStageAdmitted'), count: stageCounts.ADMITTED || 0 },
    ]
  }, [stageCounts, t])

  const filteredEnquiries = useMemo(() => {
    if (selectedStage === 'ALL') return enquiries
    return enquiries.filter((e) => e.stage === selectedStage)
  }, [enquiries, selectedStage])

  const handleMarkContacted = async (enquiry: EduEnquiry) => {
    if (!workspaceId) return
    setUpdatingId(enquiry.id)
    try {
      await setEduEnquiryStage({
        workspaceId,
        enquiryId: enquiry.id,
        stage: 'CONTACTED',
      })
      await addEduEnquiryNote({
        workspaceId,
        enquiryId: enquiry.id,
        text: 'Contacted via mobile Class tab',
      })
      await loadEnquiries()
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Failed to update enquiry')
    } finally {
      setUpdatingId(null)
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 16px' }}>
        <MiniLoader />
      </div>
    )
  }

  return (
    <div className="classAdmissionsPanel" style={{ padding: '12px 16px' }} data-testid="class-admissions-panel">
      {/* Pipeline Pills */}
      <div style={{ marginBottom: '14px', overflowX: 'auto' }}>
        <PipelinePills
          stages={pipelineStages}
          value={selectedStage}
          onChange={setSelectedStage}
          ariaLabel={t('NLSEducationAdmissionsSubtitle')}
          testId="admissions-pipeline-pills"
        />
      </div>

      {error && (
        <div style={{ color: 'var(--danger)', marginBottom: '12px', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {filteredEnquiries.length === 0 ? (
        <EmptyHubState
          title={t('NLSEduMobileAdmissionsEmptyTitle')}
          description={t('NLSEduMobileAdmissionsEmptyBody')}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }} data-testid="enquiries-list">
          {filteredEnquiries.map((enquiry) => {
            const isUpdating = updatingId === enquiry.id
            const canContact = enquiry.stage === 'NEW'
            const canAdmit = enquiry.stage !== 'ADMITTED' && enquiry.stage !== 'LOST'

            return (
              <div
                key={enquiry.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid var(--border-subtle)',
                  backgroundColor: 'var(--surface-card)',
                }}
                data-testid={`enquiry-card-${enquiry.id}`}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '14px' }}>
                      {enquiry.learner?.name || '—'}
                    </div>
                    {(enquiry.contact?.name || enquiry.contact?.phone || enquiry.contact?.email) && (
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {[
                          enquiry.contact?.name,
                          enquiry.contact?.relation,
                          enquiry.contact?.phone,
                          enquiry.contact?.email,
                        ]
                          .filter(Boolean)
                          .join(' • ')}
                      </div>
                    )}
                  </div>
                  <StatusPill
                    tone={getStageTone(enquiry.stage)}
                  >
                    {enquiry.stage}
                  </StatusPill>
                </div>

                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '4px' }}>
                  {canContact && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={isUpdating}
                      onClick={() => handleMarkContacted(enquiry)}
                      data-testid={`contact-btn-${enquiry.id}`}
                    >
                      {t('NLSEduMobileAdmissionsContacted')}
                    </Button>
                  )}
                  {canAdmit && (
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={isUpdating}
                      onClick={() => setAdmittingEnquiry(enquiry)}
                      data-testid={`admit-btn-${enquiry.id}`}
                    >
                      {t('NLSEduMobileAdmissionsAdmit')}
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Existing Admit Dialog (R21 join code flow) */}
      {admittingEnquiry && (
        <AdmitEnquiryDialog
          isOpen={Boolean(admittingEnquiry)}
          onClose={() => setAdmittingEnquiry(null)}
          enquiry={admittingEnquiry}
          workspaceId={workspaceId}
          elements={elements}
          onSuccess={() => {
            setAdmittingEnquiry(null)
            void loadEnquiries()
          }}
        />
      )}
    </div>
  )
}
